import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { Alert, Button, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStore } from 'src/stores';
import { getCurrentCompanyId } from 'src/stores/billAutomation/currentCompanyId';
import { readIntakeAddress } from 'src/stores/company/intakeAddressRead';
import { readSubscriptionTier } from 'src/stores/company/subscriptionTierRead';
import useStyles from './styles';
const COPIED = 'Intake address copied.';
const COPY_FAILED = "Couldn't copy the address. Select it and copy it.";
const LOADING = {
  companyId: null,
  status: 'loading',
  address: null
};

/**
 * Where a setup on Email has its bills sent (PRD V3, FR-6; LB-6190): the company's intake address with Copy, one address
 * for the whole company. Reading never creates one: staff set it up, and only for Plus and Autopilot (LB-6229). The
 * address is read only once the company's plan is known to include email; on a plan without it, this says what happens
 * to bills sent there instead. Shown in the Setup section under a setup on Email, and in the setup editor while Email is
 * picked; neither waits on it to save.
 */
export const IntakeAddress = observer(_ref => {
  let className = _ref.className;
  const classes = useStyles();
  const store = useStore();
  // Observed, so a company switch re-renders this and reads the new company, as the Setup section does.
  const companyAccessId = store.homeDashboardStore.companyAccessId;
  // Moves when staff rotate or generate an address, after the kept read is dropped: the new one replaces what's shown.
  const aliasRevision = store.company.billIntakeAliasRevision;
  const companyId = getCurrentCompanyId();

  // 'loading' | 'planFailed' | 'locked' | 'ready' | 'none' | 'failed', and whose it is.
  const _useState = useState(LOADING),
    _useState2 = _slicedToArray(_useState, 2),
    read = _useState2[0],
    setRead = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    attempt = _useState4[0],
    setAttempt = _useState4[1];
  useEffect(() => {
    if (!companyId) return undefined;
    // A switch or a retry can overlap a read still out, and only this one may answer.
    let active = true;
    setRead(_objectSpread(_objectSpread({}, LOADING), {}, {
      companyId
    }));
    (async _plan$allows => {
      // The plan first, through the read every badge shares: an unknown plan isn't taken as one that includes email,
      // or a Pro company's address would be read before its plan arrived (and shown, if the plan never did).
      const plan = await readSubscriptionTier(companyId);
      if (!active) return;
      if (!plan) {
        setRead({
          companyId,
          status: 'planFailed',
          address: null
        });
        return;
      }
      if (!((_plan$allows = plan.allows) !== null && _plan$allows !== void 0 && _plan$allows.emailIn)) {
        setRead({
          companyId,
          status: 'locked',
          address: null
        });
        return;
      }
      const answer = await readIntakeAddress(companyId);
      if (!active) return;
      if (!answer) setRead({
        companyId,
        status: 'failed',
        address: null
      });else setRead({
        companyId,
        status: answer.address ? 'ready' : 'none',
        address: answer.address
      });
    })();
    return () => {
      active = false;
    };
  }, [companyId, companyAccessId, aliasRevision, attempt]);
  if (!companyId) return null;

  // Until this company's own answer arrives: a switch never shows the last company's address.
  const _ref2 = read.companyId === companyId ? read : LOADING,
    status = _ref2.status,
    address = _ref2.address;
  const rootClass = className ? "".concat(classes.intake, " ").concat(className) : classes.intake;
  const handleCopy = () => {
    // Inside the click, as the clipboard asks; a browser without it refuses the same way.
    Promise.resolve().then(() => navigator.clipboard.writeText(address)).then(() => toast('success', {
      body: [COPIED]
    }), () => toast('error', {
      body: [COPY_FAILED]
    }));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: rootClass,
    "data-testid": "intake-address"
  }, status === 'loading' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Loading your intake address\u2026"), status === 'locked' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "Your plan doesn't include bills by email. Once its grace period ends, bills sent to your company's intake address wait on the Bills page until you upgrade."), status === 'ready' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    className: classes.intakeLabel
  }, "Your company's intake address"), /*#__PURE__*/React.createElement("div", {
    className: classes.intakeRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.intakeAddress
  }, address), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: handleCopy
  }, "Copy")), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Forward bills here, or give this address to the provider. One address for the whole company.")), status === 'none' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "Your company doesn't have an intake address yet. Livable sets one up. Until then, bills can't arrive by email."), (status === 'failed' || status === 'planFailed') && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setAttempt(n => n + 1)
    }, "Retry")
  }, status === 'planFailed' ? "Couldn't load your company's plan, so the intake address isn't shown yet." : "Couldn't load your intake address."));
});